import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Users, Plus, Search, Edit, Trash2, Phone, Mail, MapPin } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClientsPage = () => {
  const { getAuthHeaders } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: ''
  });

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`, getAuthHeaders());
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
      toast.error('Erreur lors du chargement des clients');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      address: ''
    });
    setEditingClient(null);
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name || '',
      email: client.email || '',
      phone: client.phone || '',
      address: client.address || ''
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Le nom du client est obligatoire');
      return;
    }

    setSaving(true);
    try {
      if (editingClient) {
        // Update existing client
        await axios.put(
          `${API}/clients/${editingClient.id}`,
          formData,
          getAuthHeaders()
        );
        toast.success('Client mis à jour avec succès');
      } else {
        // Create new client
        await axios.post(`${API}/clients`, formData, getAuthHeaders());
        toast.success('Client créé avec succès');
      }
      setShowModal(false);
      resetForm();
      fetchClients();
    } catch (error) {
      console.error('Error saving client:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!clientToDelete) return;

    setDeleting(true);
    try {
      await axios.delete(`${API}/clients/${clientToDelete.id}`, getAuthHeaders());
      toast.success('Client supprimé avec succès');
      setClientToDelete(null);
      fetchClients();
    } catch (error) {
      console.error('Error deleting client:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  // Filter clients based on search
  const filteredClients = clients.filter(client =>
    client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (client.email && client.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (client.phone && client.phone.includes(searchTerm))
  );

  if (loading) {
    return <div className="flex items-center justify-center h-full">Chargement...</div>;
  }

  return (
    <div className="space-y-6" data-testid="clients-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold text-[#1B3268] tracking-tight flex items-center gap-3" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            <Users className="w-10 h-10" />
            Gestion des Clients
          </h1>
          <p className="text-sm text-slate-600 mt-1">Gérez votre base de clients</p>
        </div>
        <Button
          onClick={openAddModal}
          data-testid="add-client-button"
          className="bg-[#1B3268] hover:bg-[#152a55] text-white font-bold rounded-sm shadow-lg h-12 px-6"
        >
          <Plus className="w-5 h-5 mr-2" />
          Nouveau Client
        </Button>
      </div>

      {/* Search Bar */}
      <Card className="p-4 bg-white border border-slate-200 rounded-sm">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
          <Input
            type="text"
            placeholder="Rechercher un client (nom, email, téléphone)..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 border-slate-300"
            data-testid="search-clients"
          />
        </div>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-4 bg-gradient-to-r from-[#1B3268] to-[#2a4a8f] text-white rounded-sm">
          <div className="text-sm text-white/70">Total Clients</div>
          <div className="text-3xl font-bold mt-1">{clients.length}</div>
        </Card>
        <Card className="p-4 bg-gradient-to-r from-[#FDC800] to-[#e5b600] text-[#1B3268] rounded-sm">
          <div className="text-sm text-[#1B3268]/70">Avec Email</div>
          <div className="text-3xl font-bold mt-1">{clients.filter(c => c.email).length}</div>
        </Card>
        <Card className="p-4 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-sm">
          <div className="text-sm text-white/70">Avec Téléphone</div>
          <div className="text-3xl font-bold mt-1">{clients.filter(c => c.phone).length}</div>
        </Card>
      </div>

      {/* Clients Table */}
      <Card className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden" data-testid="clients-table">
        <div className="border-b border-slate-100 p-4 bg-slate-50/50">
          <h3 className="text-xl font-semibold text-[#1B3268]">Liste des Clients</h3>
          <p className="text-sm text-slate-500 mt-1">{filteredClients.length} client(s) trouvé(s)</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-100 text-slate-700 font-semibold uppercase text-xs tracking-wider">
                <th className="p-4 text-left">Nom</th>
                <th className="p-4 text-left">Email</th>
                <th className="p-4 text-left">Téléphone</th>
                <th className="p-4 text-left">Adresse</th>
                <th className="p-4 text-left">Date d'ajout</th>
                <th className="p-4 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredClients.map((client) => (
                <tr key={client.id} className="hover:bg-slate-50 transition-colors border-b last:border-0" data-testid={`client-row-${client.id}`}>
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#1B3268] flex items-center justify-center text-white font-bold">
                        {client.name.charAt(0).toUpperCase()}
                      </div>
                      <span className="font-medium text-[#1B3268]">{client.name}</span>
                    </div>
                  </td>
                  <td className="p-4">
                    {client.email ? (
                      <div className="flex items-center gap-2 text-slate-600">
                        <Mail className="w-4 h-4 text-slate-400" />
                        {client.email}
                      </div>
                    ) : (
                      <span className="text-slate-400">-</span>
                    )}
                  </td>
                  <td className="p-4">
                    {client.phone ? (
                      <div className="flex items-center gap-2 text-slate-600">
                        <Phone className="w-4 h-4 text-slate-400" />
                        {client.phone}
                      </div>
                    ) : (
                      <span className="text-slate-400">-</span>
                    )}
                  </td>
                  <td className="p-4">
                    {client.address ? (
                      <div className="flex items-center gap-2 text-slate-600 max-w-xs truncate">
                        <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        <span className="truncate">{client.address}</span>
                      </div>
                    ) : (
                      <span className="text-slate-400">-</span>
                    )}
                  </td>
                  <td className="p-4 text-slate-600 text-sm">
                    {new Date(client.created_at).toLocaleDateString('fr-FR')}
                  </td>
                  <td className="p-4">
                    <div className="flex items-center justify-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditModal(client)}
                        className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                        data-testid={`edit-client-${client.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setClientToDelete(client)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-client-${client.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredClients.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              {searchTerm ? 'Aucun client trouvé pour cette recherche' : 'Aucun client enregistré'}
            </div>
          )}
        </div>
      </Card>

      {/* Add/Edit Client Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-[500px]" data-testid="client-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#1B3268] flex items-center gap-2">
              {editingClient ? <Edit className="w-6 h-6" /> : <Plus className="w-6 h-6" />}
              {editingClient ? 'Modifier le Client' : 'Nouveau Client'}
            </DialogTitle>
            <DialogDescription>
              {editingClient ? 'Modifiez les informations du client' : 'Ajoutez un nouveau client à votre base'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm font-medium text-slate-700">
                Nom du client *
              </Label>
              <Input
                id="name"
                placeholder="Ex: Entreprise ABC"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                data-testid="client-name-input"
                className="border-slate-300"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                <Mail className="w-4 h-4 inline mr-1" />
                Email
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="Ex: contact@entreprise.com"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                data-testid="client-email-input"
                className="border-slate-300"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="text-sm font-medium text-slate-700">
                <Phone className="w-4 h-4 inline mr-1" />
                Téléphone
              </Label>
              <Input
                id="phone"
                type="tel"
                placeholder="Ex: +221 77 123 45 67"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                data-testid="client-phone-input"
                className="border-slate-300"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="address" className="text-sm font-medium text-slate-700">
                <MapPin className="w-4 h-4 inline mr-1" />
                Adresse
              </Label>
              <Input
                id="address"
                placeholder="Ex: 123 Rue Principale, Dakar"
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                data-testid="client-address-input"
                className="border-slate-300"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowModal(false);
                resetForm();
              }}
              data-testid="cancel-client"
            >
              Annuler
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              data-testid="save-client"
              className="bg-[#1B3268] hover:bg-[#152a55] text-white"
            >
              {saving ? 'Enregistrement...' : (editingClient ? 'Mettre à jour' : 'Créer le client')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!clientToDelete} onOpenChange={() => setClientToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Supprimer ce client ?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {clientToDelete && (
                <div className="space-y-2 mt-2">
                  <p>Vous êtes sur le point de supprimer le client suivant :</p>
                  <div className="bg-slate-50 p-3 rounded-md text-sm">
                    <p><strong>Nom :</strong> {clientToDelete.name}</p>
                    {clientToDelete.email && <p><strong>Email :</strong> {clientToDelete.email}</p>}
                    {clientToDelete.phone && <p><strong>Téléphone :</strong> {clientToDelete.phone}</p>}
                  </div>
                  <p className="text-amber-600 font-medium mt-3">
                    ⚠️ Cette action est irréversible.
                  </p>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deleting ? 'Suppression...' : 'Supprimer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ClientsPage;
